import { createContext, useContext } from "react";

export const LangContext = createContext({ lang: "it", setLang: () => {}, t: {} });
export const useLang = () => useContext(LangContext);

export const translations = {
  it: {
    nav: {
      method: "Il metodo",
      programs: "Programmi",
      community: "Community",
      ecosystem: "Ecosistema",
      faq: "FAQ",
      cta: "Richiedi una demo",
      menuOpen: "Apri il menu",
      menuClose: "Chiudi il menu",
    },
    hero: {
      eyebrow: "TRAIN SMART · MOVE TOGETHER",
      lines: ["Allenati", "meglio.", "Insieme."],
      lead: "Un programma costruito intorno a te. Una community che ti motiva, ti sfida e trasforma ogni settimana in un nuovo traguardo.",
      primary: "Scopri come funziona",
      secondary: "Per trainer e palestre",
      statusTag: "ESPERIENZA CONNESSA",
      statusNum: "01",
      statusText: "Un unico ecosistema per allenarsi, progredire e condividere l'esperienza.",
      bottom: ["Cross training", "Powerlifting", "AI coaching", "Community"],
    },
    manifesto: {
      index: "01 / VISION",
      kicker: "NON IL SOLITO PROGRAMMA",
      titleA: "La tecnologia costruisce il percorso.",
      titleB: "Le persone fanno la differenza.",
      p1: "Tech Training nasce dall'evoluzione di Cross Training Coach: non solo una scheda digitale, ma un'esperienza capace di unire programmazione personale, relazione con il trainer e forza del gruppo.",
      p2: "Perché la costanza non dipende soltanto da cosa fai. Dipende da quanto ti senti seguito, coinvolto e parte di qualcosa.",
    },
    marquee: ["PROGRAMMI SU MISURA", "CHALLENGE SETTIMANALI", "PROGRESSI VISIBILI", "COMMUNITY ATTIVA", "AI COACHING", "POWERLIFTING"],
    stats: {
      note: "Numeri dimostrativi della piattaforma",
      items: [
        { value: 45000, suffix: "+", label: "WOD completati" },
        { value: 12500, suffix: "+", label: "Atleti attivi" },
        { value: 98.4, suffix: "%", decimals: 1, label: "Precisione suggerimenti AI" },
        { value: 320, suffix: "+", label: "Box e palestre" },
      ],
    },
    method: {
      index: "02 / METHOD",
      kicker: "IL TUO ALLENAMENTO, DAVVERO TUO",
      titleA: "Tu dai l'obiettivo.",
      titleB: "Tech Training disegna la strada.",
      steps: [
        { title: "Raccontaci chi sei", desc: "Obiettivi, esperienza, disponibilità, attrezzatura e preferenze." },
        { title: "Ricevi il tuo piano", desc: "Sessioni chiare e progressive, costruite intorno al tuo punto di partenza." },
        { title: "Allenati e misura", desc: "Registra risultati, percezione dello sforzo e progressi nel tempo." },
        { title: "Entra nella sfida", desc: "Challenge, classifiche e obiettivi condivisi danno ritmo alla settimana." },
      ],
      states: [
        {
          kicker: "IL TUO ALLENAMENTO", title: "Power & Engine", ring: ["42", "MIN"], tags: ["Intermedio", "Forza + MetCon"],
          rows: [["01", "Front squat", "4 × 6 · controllo", "✓"], ["02", "Box jump", "4 × 10 · esplosività", "→"], ["03", "Row intervals", "6 × 45” · ritmo", "→"]],
          note: ["COACH NOTE", "Oggi punta sulla qualità del movimento. Il carico viene dopo."],
        },
        {
          kicker: "LA TUA SETTIMANA", title: "Agenda smart", ring: ["04", "SESSIONI"], tags: ["Questa settimana", "1 recupero"],
          rows: [["LUN", "Strength base", "Completato · 48 min", "✓"], ["MER", "Engine builder", "Oggi · 42 min", "→"], ["SAB", "Community workout", "Con il tuo team", "＋"]],
          note: ["SMART PLAN", "La settimana si adatta ai tuoi giorni disponibili e al recupero."],
        },
        {
          kicker: "I TUOI RISULTATI", title: "Progressi visibili", ring: ["+8", "% TREND"], tags: ["6 settimane", "Costanza 84%"],
          rows: [["01", "Forza", "Trend in crescita", "↗"], ["02", "Condizionamento", "Nuovo record personale", "↗"], ["03", "Mobilità", "Obiettivo da consolidare", "→"]],
          note: ["INSIGHT", "Il tuo ritmo è costante. Mantieni una giornata di recupero attivo."],
        },
        {
          kicker: "COMMUNITY", title: "Weekly challenge", ring: ["12", "MIN"], tags: ["42 partecipanti", "Termina domenica"],
          rows: [["01", "Full Send / 12", "AMRAP · 3 movimenti", "→"], ["02", "Il tuo risultato", "198 ripetizioni", "12°"], ["03", "Team goal", "Mancano 840 rep", "↗"]],
          note: ["COMMUNITY BOOST", "Sei a 8 ripetizioni dalla top 10. Hai ancora due giorni."],
        },
      ],
      floatOne: "Piano aggiornato",
      floatTwo: "6 settimane",
      phoneLabel: "Anteprima dell'app Tech Training",
    },
    programs: {
      index: "03 / PROGRAMS",
      kicker: "TRE VIE, UN SISTEMA",
      titleA: "Scegli la disciplina.",
      titleB: "Al resto pensa Tech Training.",
      cards: [
        {
          tag: "CROSS TRAINING", title: "Motore e potenza.", cls: "large",
          img: "https://images.unsplash.com/photo-1606335544665-96055053b5c0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHxjcm9zc2ZpdCUyMHBvd2VybGlmdGluZyUyMGF0aGxldGUlMjBoZWF2eSUyMGd5bSUyMG5lb24lMjB3b3Jrb3V0fGVufDB8fHx8MTc5MTQ2NjcyNHww&ixlib=rb-4.1.0&q=85",
          text: "WOD ad alta intensità che combinano forza, resistenza e skill ginnastiche. Ogni settimana uno stimolo nuovo.",
          toggle: "Schede esercizio", close: "Chiudi",
          exercises: [["Kettlebell swing + Thruster", "21-15-9 · per tempo"], ["Wall ball", "EMOM 12' · 12 rep"], ["Burpee box jump", "AMRAP 20' · 10 rep"]],
        },
        {
          tag: "POWERLIFTING", title: "Forza pura.", cls: "small",
          img: "https://images.unsplash.com/photo-1606335545623-5d90040a4756?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHw0fHxjcm9zc2ZpdCUyMHBvd2VybGlmdGluZyUyMGF0aGxldGUlMjBoZWF2eSUyMGd5bSUyMG5lb24lMjB3b3Jrb3V0fGVufDB8fHx8MTc5MTQ2NjcyNHww&ixlib=rb-4.1.0&q=85",
          text: "Programmazione a percentuali su squat, panca e stacco, con progressioni misurabili.",
          toggle: "Schede esercizio", close: "Chiudi",
          exercises: [["Back squat", "5 × 5 · 80% 1RM"], ["Panca piana", "4 × 6 · 77% 1RM"], ["Stacco da terra", "3 × 3 · 85% 1RM"]],
        },
        {
          tag: "AI COACHING", title: "Il piano che impara.", cls: "wide",
          img: "https://images.unsplash.com/photo-1709315969349-1a43dea3efdc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxjcm9zc2ZpdCUyMHBvd2VybGlmdGluZyUyMGF0aGxldGUlMjBoZWF2eSUyMGd5bSUyMG5lb24lMjB3b3Jrb3V0fGVufDB8fHx8MTc5MTQ2NjcyNHww&ixlib=rb-4.1.0&q=85",
          text: "L'algoritmo osserva risultati, sforzo percepito e recupero: il programma si aggiorna con te, sessione dopo sessione.",
          toggle: "Come lavora", close: "Chiudi",
          exercises: [["Analisi del carico", "Suggerimenti sui pesi, set-by-set"], ["Recupero adattivo", "Il piano si ricalibra ogni settimana"], ["Feedback del coach", "Note sul movimento dopo ogni sessione"]],
        },
      ],
    },
    community: {
      index: "04 / COMMUNITY",
      kicker: "LA PARTE CHE NON PUOI FARE DA SOLO",
      titleA: "Ti alleni per te.",
      titleB: "Ma non ti alleni da solo.",
      challengeLabel: "CHALLENGE DELLA SETTIMANA",
      challengeName: "FULL SEND / 12",
      challengeUnit: "MINUTI AMRAP",
      moves: [["12", "Kettlebell swing"], ["10", "Box step-over"], ["08", "Burpee"]],
      ends: "Termina domenica",
      countdown: { label: "LA SFIDA TERMINA TRA", units: ["giorni", "ore", "min", "sec"] },
      boardLabel: "CLASSIFICA COMMUNITY",
      boardTitle: "Top performer",
      week: "W. 38",
      rows: [
        { rank: "01", initials: "AR", color: "blue", name: "Andrea R.", sub: "Tech Training Milano", reps: "248 rep" },
        { rank: "02", initials: "MC", color: "lime", name: "Marta C.", sub: "Functional Lab", reps: "241 rep" },
        { rank: "03", initials: "LS", color: "coral", name: "Luca S.", sub: "Independent athlete", reps: "229 rep" },
      ],
      you: { rank: "12", initials: "TU", name: "Il tuo risultato", sub: "Continua così", reps: "198 rep" },
      demoNote: "Dati dimostrativi dell'interfaccia.",
      features: [
        { icon: "↗", title: "Challenge che accendono la settimana", text: "Obiettivi sempre nuovi, individuali o di squadra, per allenare costanza e spirito competitivo." },
        { icon: "◎", title: "Progressi che vale la pena condividere", text: "Risultati, traguardi e record personali diventano occasioni per supportarsi e celebrare." },
        { icon: "✦", title: "Competizione positiva, sempre", text: "Classifiche e giochi interattivi aggiungono energia senza perdere di vista il percorso personale." },
      ],
    },
    audiences: {
      index: "05 / ECOSYSTEM",
      kicker: "UN ECOSISTEMA, TRE PROSPETTIVE",
      titleA: "Tutto ciò che serve",
      titleB: "a chi si allena e a chi allena.",
      cards: [
        { num: "01", tag: "ATLETI", title: "Il percorso è tuo.", text: "Allenamenti comprensibili, obiettivi chiari e una community pronta a tenerti in movimento.", list: ["Piano personalizzato", "Agenda e storico sessioni", "Monitoraggio dei progressi", "Challenge e classifiche"], symbol: "A", cls: "athlete" },
        { num: "02", tag: "TRAINER", title: "Più persone. Più controllo.", text: "Organizza la programmazione, segui gli atleti e intervieni quando il tuo supporto conta davvero.", list: ["Gestione atleti e gruppi", "Programmazione e calendario", "Risultati e feedback", "Comunicazioni centralizzate"], symbol: "T", cls: "trainer" },
        { num: "03", tag: "PALESTRE & BOX", title: "Una community che cresce.", text: "Coordina attività e persone da un unico portale, creando un'esperienza continua dentro e fuori la palestra.", list: ["Dashboard centralizzata", "Team e ruoli dedicati", "Eventi e challenge interne", "Engagement della community"], symbol: "G", cls: "gym" },
      ],
    },
    portal: {
      index: "06 / CONTROL",
      kicker: "PER TRAINER E PALESTRE",
      titleA: "La tua regia.",
      titleB: "Un'unica dashboard.",
      text: "Il portale Tech Training concentra persone, attività e risultati in uno spazio ordinato. Una visione d'insieme per gestire meglio; il dettaglio giusto per seguire ogni atleta.",
      list: ["Anagrafiche, gruppi e livelli", "Programmi, calendario e assegnazioni", "Performance, continuità e feedback", "Challenge, notifiche e community"],
      dash: {
        date: "GIOVEDÌ, 08 OTTOBRE",
        greeting: "Buon allenamento, Coach.",
        add: "＋ Aggiungi",
        stats: [["Atleti attivi", "128", "↑ demo"], ["Sessioni settimana", "346", "82% completate"], ["Challenge attive", "04", "2 in scadenza"]],
        chart: "Attività settimanale",
        chartSub: "Sessioni",
        upNext: "Prossime attività",
        today: "Oggi",
        events: [["18:00", "Functional class", "12 / 16 atleti"], ["19:15", "Performance team", "8 / 10 atleti"], ["20:30", "Open training", "6 / 12 atleti"]],
        follow: "Atleti da seguire",
        seeAll: "Vedi tutti →",
        athletes: [
          { initials: "MC", color: "lime", name: "Marta C.", sub: "Performance", sessions: "3 sessioni", tag: "Ottimo ritmo", attention: false },
          { initials: "AR", color: "blue", name: "Andrea R.", sub: "Cross training", sessions: "1 sessione", tag: "Da ricontattare", attention: true },
        ],
        menu: ["Overview", "Atleti", "Programmi", "Calendario", "Challenge"],
        label: "ANTEPRIMA INTERFACCIA · DATI DIMOSTRATIVI",
      },
    },
    values: {
      index: "07 / WHY",
      kicker: "PERCHÉ TECH TRAINING",
      titleA: "La motivazione cambia.",
      titleB: "Il sistema resta.",
      items: [
        { title: "Personalizzazione", text: "Un percorso coerente con il livello, il tempo disponibile e gli obiettivi di ogni persona." },
        { title: "Continuità", text: "Agenda, progressi e nuovi stimoli aiutano a trasformare l'allenamento in un'abitudine." },
        { title: "Relazione", text: "Il digitale avvicina atleta, trainer e palestra, rendendo il supporto più semplice e puntuale." },
        { title: "Coinvolgimento", text: "Challenge e dinamiche social rendono l'esperienza più viva, partecipata e memorabile." },
      ],
    },
    faq: {
      index: "08 / FAQ",
      kicker: "DOMANDE FREQUENTI",
      titleA: "Prima di",
      titleB: "iniziare.",
      items: [
        { q: "Tech Training è adatto anche a chi comincia?", a: "Sì. Il percorso parte dal livello reale della persona e può adattare intensità, durata e complessità delle sessioni." },
        { q: "Serve essere iscritti a una palestra?", a: "Non necessariamente. La programmazione può considerare l'attrezzatura disponibile e il luogo in cui ci si allena." },
        { q: "Che cosa può gestire un trainer?", a: "Atleti, gruppi, programmi, calendario, risultati, feedback e iniziative community, da un unico ambiente di lavoro." },
        { q: "Una palestra può creare challenge interne?", a: "Sì. L'ecosistema è pensato per attivare competizioni, obiettivi condivisi e momenti di coinvolgimento dedicati alla propria community." },
        { q: "Tech Training sostituisce il trainer?", a: "No. La tecnologia organizza dati e percorso; il trainer mantiene il ruolo centrale di guida, relazione e supervisione." },
      ],
    },
    contact: {
      eyebrow: "READY WHEN YOU ARE",
      titleA: "Il prossimo livello",
      titleB: "inizia da qui.",
      sub: "Scopri Tech Training come atleta, trainer o palestra.",
      emailLabel: "Oppure scrivici:",
      formTitle: "Richiedi una demo",
      formSub: "Ti ricontattiamo entro 48 ore lavorative.",
      name: "Nome e cognome",
      namePh: "Mario Rossi",
      email: "Email",
      emailPh: "mario@esempio.it",
      role: "Chi sei",
      roles: ["Atleta", "Trainer / Coach", "Palestra / Box"],
      message: "Messaggio (facoltativo)",
      messagePh: "Raccontaci il tuo obiettivo…",
      submit: "Invia la richiesta",
      sending: "Invio in corso…",
      success: "Richiesta inviata! Ti ricontatteremo presto.",
      error: "Qualcosa è andato storto. Riprova tra poco.",
    },
    footer: {
      tagline: "Allenamento intelligente. Energia condivisa.",
      nav: ["Il metodo", "Programmi", "Community", "Per professionisti", "FAQ"],
      rights: "Tech Training",
      project: "Tech Training — Tutti i diritti riservati · Bagnaria Arsa (UD), Via Monsignor O. De Liva 5 · P.IVA e C.F. 03209430309 · c.s. 35.000,00 Euro",
    },
  },
  en: {
    nav: {
      method: "The method",
      programs: "Programs",
      community: "Community",
      ecosystem: "Ecosystem",
      faq: "FAQ",
      cta: "Request a demo",
      menuOpen: "Open menu",
      menuClose: "Close menu",
    },
    hero: {
      eyebrow: "TRAIN SMART · MOVE TOGETHER",
      lines: ["Train", "better.", "Together."],
      lead: "A program built around you. A community that motivates you, challenges you, and turns every week into a new milestone.",
      primary: "See how it works",
      secondary: "For trainers & gyms",
      statusTag: "CONNECTED EXPERIENCE",
      statusNum: "01",
      statusText: "One ecosystem to train, progress and share the experience.",
      bottom: ["Cross training", "Powerlifting", "AI coaching", "Community"],
    },
    manifesto: {
      index: "01 / VISION",
      kicker: "NOT YOUR USUAL PROGRAM",
      titleA: "Technology builds the path.",
      titleB: "People make the difference.",
      p1: "Tech Training evolves from Cross Training Coach: not just a digital workout card, but an experience that blends personal programming, the bond with your trainer and the strength of the group.",
      p2: "Because consistency doesn't only depend on what you do. It depends on how guided, involved and part of something you feel.",
    },
    marquee: ["TAILORED PROGRAMS", "WEEKLY CHALLENGES", "VISIBLE PROGRESS", "ACTIVE COMMUNITY", "AI COACHING", "POWERLIFTING"],
    stats: {
      note: "Demo platform figures",
      items: [
        { value: 45000, suffix: "+", label: "Completed WODs" },
        { value: 12500, suffix: "+", label: "Active athletes" },
        { value: 98.4, suffix: "%", decimals: 1, label: "AI suggestion accuracy" },
        { value: 320, suffix: "+", label: "Boxes & gyms" },
      ],
    },
    method: {
      index: "02 / METHOD",
      kicker: "YOUR TRAINING, TRULY YOURS",
      titleA: "You set the goal.",
      titleB: "Tech Training draws the road.",
      steps: [
        { title: "Tell us who you are", desc: "Goals, experience, availability, equipment and preferences." },
        { title: "Get your plan", desc: "Clear, progressive sessions built around your starting point." },
        { title: "Train and measure", desc: "Log results, perceived effort and progress over time." },
        { title: "Join the challenge", desc: "Challenges, leaderboards and shared goals set the week's rhythm." },
      ],
      states: [
        {
          kicker: "YOUR WORKOUT", title: "Power & Engine", ring: ["42", "MIN"], tags: ["Intermediate", "Strength + MetCon"],
          rows: [["01", "Front squat", "4 × 6 · control", "✓"], ["02", "Box jump", "4 × 10 · explosiveness", "→"], ["03", "Row intervals", "6 × 45” · pace", "→"]],
          note: ["COACH NOTE", "Focus on movement quality today. Load comes later."],
        },
        {
          kicker: "YOUR WEEK", title: "Smart agenda", ring: ["04", "SESSIONS"], tags: ["This week", "1 recovery"],
          rows: [["MON", "Strength base", "Completed · 48 min", "✓"], ["WED", "Engine builder", "Today · 42 min", "→"], ["SAT", "Community workout", "With your team", "＋"]],
          note: ["SMART PLAN", "The week adapts to your available days and recovery."],
        },
        {
          kicker: "YOUR RESULTS", title: "Visible progress", ring: ["+8", "% TREND"], tags: ["6 weeks", "84% consistency"],
          rows: [["01", "Strength", "Upward trend", "↗"], ["02", "Conditioning", "New personal record", "↗"], ["03", "Mobility", "Goal to consolidate", "→"]],
          note: ["INSIGHT", "Your pace is steady. Keep one active recovery day."],
        },
        {
          kicker: "COMMUNITY", title: "Weekly challenge", ring: ["12", "MIN"], tags: ["42 participants", "Ends Sunday"],
          rows: [["01", "Full Send / 12", "AMRAP · 3 movements", "→"], ["02", "Your result", "198 reps", "12th"], ["03", "Team goal", "840 reps to go", "↗"]],
          note: ["COMMUNITY BOOST", "You're 8 reps from the top 10. Two days left."],
        },
      ],
      floatOne: "Plan updated",
      floatTwo: "6 weeks",
      phoneLabel: "Tech Training app preview",
    },
    programs: {
      index: "03 / PROGRAMS",
      kicker: "THREE PATHS, ONE SYSTEM",
      titleA: "Pick your discipline.",
      titleB: "Tech Training handles the rest.",
      cards: [
        {
          tag: "CROSS TRAINING", title: "Engine and power.", cls: "large",
          img: "https://images.unsplash.com/photo-1606335544665-96055053b5c0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHxjcm9zc2ZpdCUyMHBvd2VybGlmdGluZyUyMGF0aGxldGUlMjBoZWF2eSUyMGd5bSUyMG5lb24lMjB3b3Jrb3V0fGVufDB8fHx8MTc5MTQ2NjcyNHww&ixlib=rb-4.1.0&q=85",
          text: "High-intensity WODs blending strength, endurance and gymnastic skills. A fresh stimulus every week.",
          toggle: "Exercise cards", close: "Close",
          exercises: [["Kettlebell swing + Thruster", "21-15-9 · for time"], ["Wall ball", "EMOM 12' · 12 reps"], ["Burpee box jump", "AMRAP 20' · 10 reps"]],
        },
        {
          tag: "POWERLIFTING", title: "Pure strength.", cls: "small",
          img: "https://images.unsplash.com/photo-1606335545623-5d90040a4756?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHw0fHxjcm9zc2ZpdCUyMHBvd2VybGlmdGluZyUyMGF0aGxldGUlMjBoZWF2eSUyMGd5bSUyMG5lb24lMjB3b3Jrb3V0fGVufDB8fHx8MTc5MTQ2NjcyNHww&ixlib=rb-4.1.0&q=85",
          text: "Percentage-based programming on squat, bench and deadlift, with measurable progressions.",
          toggle: "Exercise cards", close: "Close",
          exercises: [["Back squat", "5 × 5 · 80% 1RM"], ["Bench press", "4 × 6 · 77% 1RM"], ["Deadlift", "3 × 3 · 85% 1RM"]],
        },
        {
          tag: "AI COACHING", title: "The plan that learns.", cls: "wide",
          img: "https://images.unsplash.com/photo-1709315969349-1a43dea3efdc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxjcm9zc2ZpdCUyMHBvd2VybGlmdGluZyUyMGF0aGxldGUlMjBoZWF2eSUyMGd5bSUyMG5lb24lMjB3b3Jrb3V0fGVufDB8fHx8MTc5MTQ2NjcyNHww&ixlib=rb-4.1.0&q=85",
          text: "The algorithm reads results, perceived effort and recovery: your program updates with you, session after session.",
          toggle: "How it works", close: "Close",
          exercises: [["Load analysis", "Weight suggestions, set by set"], ["Adaptive recovery", "The plan recalibrates every week"], ["Coach feedback", "Movement notes after each session"]],
        },
      ],
    },
    community: {
      index: "04 / COMMUNITY",
      titleA: "You train for yourself.",
      titleB: "But you don't train alone.",
      challengeLabel: "CHALLENGE OF THE WEEK",
      challengeName: "FULL SEND / 12",
      challengeUnit: "MINUTES AMRAP",
      moves: [["12", "Kettlebell swing"], ["10", "Box step-over"], ["08", "Burpee"]],
      ends: "Ends Sunday",
      countdown: { label: "CHALLENGE ENDS IN", units: ["days", "hrs", "min", "sec"] },
      boardLabel: "COMMUNITY LEADERBOARD",
      boardTitle: "Top performers",
      week: "W. 38",
      rows: [
        { rank: "01", initials: "AR", color: "blue", name: "Andrea R.", sub: "Tech Training Milan", reps: "248 reps" },
        { rank: "02", initials: "MC", color: "lime", name: "Marta C.", sub: "Functional Lab", reps: "241 reps" },
        { rank: "03", initials: "LS", color: "coral", name: "Luca S.", sub: "Independent athlete", reps: "229 reps" },
      ],
      you: { rank: "12", initials: "YOU", name: "Your result", sub: "Keep it up", reps: "198 reps" },
      demoNote: "Interface demo data.",
      features: [
        { icon: "↗", title: "Challenges that fire up your week", text: "Always-new goals, solo or team-based, to train consistency and competitive spirit." },
        { icon: "◎", title: "Progress worth sharing", text: "Results, milestones and PRs become chances to support each other and celebrate." },
        { icon: "✦", title: "Positive competition, always", text: "Leaderboards and interactive games add energy without losing sight of your personal path." },
      ],
    },
    audiences: {
      index: "05 / ECOSYSTEM",
      kicker: "ONE ECOSYSTEM, THREE PERSPECTIVES",
      titleA: "Everything you need",
      titleB: "to train and to coach.",
      cards: [
        { num: "01", tag: "ATHLETES", title: "The path is yours.", text: "Understandable workouts, clear goals and a community ready to keep you moving.", list: ["Personalized plan", "Agenda & session history", "Progress tracking", "Challenges & leaderboards"], symbol: "A", cls: "athlete" },
        { num: "02", tag: "TRAINERS", title: "More people. More control.", text: "Organize programming, follow your athletes and step in when your support truly matters.", list: ["Athlete & group management", "Programming & calendar", "Results & feedback", "Centralized communications"], symbol: "T", cls: "trainer" },
        { num: "03", tag: "GYMS & BOXES", title: "A community that grows.", text: "Coordinate activities and people from a single portal, creating a seamless experience in and out of the gym.", list: ["Centralized dashboard", "Dedicated teams & roles", "Internal events & challenges", "Community engagement"], symbol: "G", cls: "gym" },
      ],
    },
    portal: {
      index: "06 / CONTROL",
      kicker: "FOR TRAINERS & GYMS",
      titleA: "Your control room.",
      titleB: "One single dashboard.",
      text: "The Tech Training portal brings people, activities and results into one tidy space. A big picture to manage better; the right detail to follow every athlete.",
      list: ["Profiles, groups and levels", "Programs, calendar and assignments", "Performance, consistency and feedback", "Challenges, notifications and community"],
      dash: {
        date: "THURSDAY, OCTOBER 8",
        greeting: "Have a great workout, Coach.",
        add: "＋ Add",
        stats: [["Active athletes", "128", "↑ demo"], ["Weekly sessions", "346", "82% completed"], ["Active challenges", "04", "2 expiring"]],
        chart: "Weekly activity",
        chartSub: "Sessions",
        upNext: "Up next",
        today: "Today",
        events: [["18:00", "Functional class", "12 / 16 athletes"], ["19:15", "Performance team", "8 / 10 athletes"], ["20:30", "Open training", "6 / 12 athletes"]],
        follow: "Athletes to follow",
        seeAll: "See all →",
        athletes: [
          { initials: "MC", color: "lime", name: "Marta C.", sub: "Performance", sessions: "3 sessions", tag: "Great pace", attention: false },
          { initials: "AR", color: "blue", name: "Andrea R.", sub: "Cross training", sessions: "1 session", tag: "Follow up", attention: true },
        ],
        menu: ["Overview", "Athletes", "Programs", "Calendar", "Challenges"],
        label: "INTERFACE PREVIEW · DEMO DATA",
      },
    },
    values: {
      index: "07 / WHY",
      kicker: "WHY TECH TRAINING",
      titleA: "Motivation changes.",
      titleB: "The system stays.",
      items: [
        { title: "Personalization", text: "A path consistent with each person's level, available time and goals." },
        { title: "Consistency", text: "Agenda, progress and fresh stimuli help turn training into a habit." },
        { title: "Relationship", text: "Digital brings athlete, trainer and gym closer, making support simpler and timelier." },
        { title: "Engagement", text: "Challenges and social dynamics make the experience more alive, shared and memorable." },
      ],
    },
    faq: {
      index: "08 / FAQ",
      kicker: "FREQUENTLY ASKED QUESTIONS",
      titleA: "Before you",
      titleB: "start.",
      items: [
        { q: "Is Tech Training suitable for beginners?", a: "Yes. The path starts from each person's real level and can adapt intensity, duration and session complexity." },
        { q: "Do I need a gym membership?", a: "Not necessarily. Programming can take into account the equipment available and where you train." },
        { q: "What can a trainer manage?", a: "Athletes, groups, programs, calendar, results, feedback and community initiatives, from a single workspace." },
        { q: "Can a gym create internal challenges?", a: "Yes. The ecosystem is designed to activate competitions, shared goals and engagement moments for its own community." },
        { q: "Does Tech Training replace the trainer?", a: "No. Technology organizes data and the path; the trainer keeps the central role of guide, relationship and supervision." },
      ],
    },
    contact: {
      eyebrow: "READY WHEN YOU ARE",
      titleA: "The next level",
      titleB: "starts here.",
      sub: "Discover Tech Training as an athlete, trainer or gym.",
      emailLabel: "Or write to us:",
      formTitle: "Request a demo",
      formSub: "We'll get back to you within 48 working hours.",
      name: "Full name",
      namePh: "John Doe",
      email: "Email",
      emailPh: "john@example.com",
      role: "You are",
      roles: ["Athlete", "Trainer / Coach", "Gym / Box"],
      message: "Message (optional)",
      messagePh: "Tell us about your goal…",
      submit: "Send request",
      sending: "Sending…",
      success: "Request sent! We'll be in touch soon.",
      error: "Something went wrong. Please try again shortly.",
    },
    footer: {
      tagline: "Smart training. Shared energy.",
      nav: ["The method", "Programs", "Community", "For professionals", "FAQ"],
      rights: "Tech Training",
      project: "Tech Training — All rights reserved · Bagnaria Arsa (UD), Via Monsignor O. De Liva 5 · P.IVA e C.F. 03209430309 · c.s. 35.000,00 Euro",
    },
  },
};
